import { getDoc, Timestamp } from 'firebase/firestore';
import { deleteDoc, doc, setDoc } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { firestore } from '../firebase';
import GhinDaily from '../types/GhinDaily';

export const useGhinMutators = () => {
  const { isGod } = useAuth();

  const addDaily = async (daily: GhinDaily): Promise<GhinDaily | null> => {
    if (!isGod) {
      console.error('Permission denied: only god can do the global update.');
      return null;
    }

    const date = (daily.date as Timestamp).toDate();
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;

    try {
      console.log("ghin Date:", dateStr);
      const dailyRef = doc(firestore, `ghin/${year}/daily/${dateStr}`);
      console.log("Writing to path:", `ghin/${year}/daily/${dateStr}`);
      await setDoc(dailyRef, {...daily, arePersonIdsMatched: false});
      console.log("setDoc completed successfully");
      return daily;
    } catch (error) {
      console.error('Error adding daily GHIN data:', error);
      return null;
    }
  };

  const deleteDaily = async (date: Date): Promise<void> => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;

    if (!isGod) {
      console.error('Permission denied: only gods can delete ghin data');
      return;
    }

    try {
      const dailyRef = doc(firestore, `ghin/${year}/daily/${dateStr}`);
      await deleteDoc(dailyRef);
    } catch (error) {
      console.error('Error deleting ghin:', error);
    }
  };

  const updateDaily = async (daily: Partial<GhinDaily>): Promise<void> => {
    if (!isGod) {
      console.error('Permission denied: only gods can update ghin data');
      return;
    }

    if (!daily.date) {
      console.error('Date is required for updating daily GHIN data');
      return;
    }

    const date = (daily.date as Timestamp).toDate();
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;

    try {
      const dailyRef = doc(firestore, `ghin/${year}/daily/${dateStr}`);
      const existingDoc = await getDoc(dailyRef);

      if (!existingDoc.exists()) {
        console.error('Daily GHIN data not found');
        return;
      }

      const existingData = existingDoc.data() as GhinDaily;
      const updatedData = {
        ...existingData,
        indices: daily.indices ?? existingData.indices,
      };

      await setDoc(dailyRef, updatedData);
    } catch (error) {
      console.error('Error updating daily GHIN data:', error);
    }
  };

  const copyAnnualMapping = async (year: number): Promise<boolean> => {
    if (!isGod) {
      console.error('Permission denied: only god can copy annual mappings.');
      return false;
    }

    try {
      const annualRef = doc(firestore, `ghin/${year}`);
      await setDoc(annualRef, { generateMappings: true }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error requesting mapping copy:', error);
      return false;
    }
  };

  return {
    addDaily,
    copyAnnualMapping,
    deleteDaily,
    updateDaily,
  };
};
